import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ListPagination from "@/components/ListPagination";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { Button } from "@/components/ui/button";
import { Plus, FileText, Trash2, Edit3 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmDialogContext";

export default function TemplatesPage() {
  const confirm = useConfirm();
  const navigate = useNavigate();

  const fetcher = useCallback((page, limit) => api.get("/templates", { params: { page, limit } }), []);

  const {
    items,
    total,
    pages,
    page,
    limit,
    setPage,
    setLimit,
    loading,
    reload,
  } = usePaginatedList(fetcher, []);

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete template?",
      description: "Delete this template? This cannot be undone.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/templates/${id}`);
    toast.success("Template deleted");
    reload();
  };

  return (
    <div data-testid="templates-page">
      <PageHeader
        title="Templates"
        subtitle="Save email designs you can reuse across campaigns."
        action={
          <Button
            onClick={() => navigate("/templates/new")}
            className="rounded-sm h-11 btn-brand font-semibold"
            data-testid="new-template-button"
          >
            <Plus className="h-4 w-4 mr-2" /> New template
          </Button>
        }
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>
        ) : total === 0 ? (
          <div className="lift-card bg-white dark:bg-neutral-950 p-12 rounded-sm text-center">
            <FileText className="h-10 w-10 text-neutral-300 mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">No templates yet</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">Create reusable email designs to speed up future campaigns.</p>
            <Button onClick={() => navigate("/templates/new")} className="rounded-sm btn-brand font-semibold">
              <Plus className="h-4 w-4 mr-2" /> Create template
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((t) => (
              <div key={t.id} className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm" data-testid={`template-card-${t.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="h-10 w-10 rounded-sm flex items-center justify-center" style={{ backgroundColor: "var(--bg-muted)" }}>
                    <FileText className="h-5 w-5 text-neutral-700 dark:text-neutral-300" />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/templates/${t.id}/edit`)}
                      className="text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50"
                      data-testid={`template-edit-${t.id}`}
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(t.id)} className="text-neutral-400 hover:text-red-600" data-testid={`template-delete-${t.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <h3 className="font-display font-bold text-xl tracking-tight mb-2">{t.name}</h3>
                {t.preview_text && <p className="text-sm text-neutral-500 dark:text-neutral-400 line-clamp-2 mb-3">{t.preview_text}</p>}
                <div className="text-xs text-neutral-400">Created {new Date(t.created_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
        {!loading && total > 0 && (
          <ListPagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={setLimit}
            className="mt-4 rounded-sm border border-neutral-200 dark:border-neutral-800"
          />
        )}
      </div>
    </div>
  );
}
